import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Text } from 'rebass';
import colors from '../../theme/colors';

export type AnchorMenuProps = {
  id: string;
  triggerRef: React.RefObject<HTMLElement | null>;
  ariaLabel: string;
  title?: React.ReactNode;
  children: React.ReactNode;
  panelWidthPx?: number;
  placement?: 'bottom' | 'top' | 'top-right';
  showCaret?: boolean;
  caretEdgeInset?: number;
  viewportMarginPx?: number;
  layoutDependency?: unknown;
  outsidePointerBoundaryRef?: React.RefObject<HTMLElement | null>;
  onDismiss?: () => void;
};

const AnchorMenu = ({
  id,
  triggerRef,
  ariaLabel,
  title,
  children,
  panelWidthPx = 380,
  placement = 'bottom',
  showCaret = true,
  caretEdgeInset = 20,
  viewportMarginPx = 12,
  layoutDependency,
  outsidePointerBoundaryRef,
  onDismiss,
}: AnchorMenuProps): JSX.Element => {
  const outerRef = useRef<HTMLDivElement>(null);
  const [panelLayout, setPanelLayout] = useState<{
    leftPx: number;
    widthPx: number;
    caretLeftPx: number;
    bottomPx?: number;
    maxHeightPx?: number;
  } | null>(null);

  useLayoutEffect(() => {
    const updateLayout = () => {
      const triggerEl = triggerRef.current;
      const outerEl = outerRef.current;
      if (!triggerEl || !outerEl) {
        return;
      }
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const margin = viewportMarginPx;
      const maxRight = vw - margin;
      const triggerR = triggerEl.getBoundingClientRect();

      if (placement === 'top' || placement === 'top-right') {
        const baseWidth = Math.min(panelWidthPx, vw - 2 * margin);
        let menuWidth = baseWidth;
        let menuLeftV = triggerR.right - menuWidth;

        if (placement === 'top-right') {
          const triggerCenterX = triggerR.left + triggerR.width / 2;
          const preferredLeft = triggerCenterX - Math.max(6, caretEdgeInset / 2);
          const availableRightWidth = maxRight - preferredLeft;
          menuWidth =
            availableRightWidth >= Math.min(baseWidth, 280)
              ? Math.min(baseWidth, availableRightWidth)
              : baseWidth;
          menuLeftV = Math.max(
            margin,
            Math.min(preferredLeft, maxRight - menuWidth)
          );
        } else if (menuLeftV < margin) {
          menuLeftV = margin;
          menuWidth = Math.min(menuWidth, maxRight - menuLeftV);
        } else if (menuLeftV + menuWidth > maxRight) {
          menuLeftV = maxRight - menuWidth;
        }
        if (placement === 'top') {
          menuLeftV = Math.min(menuLeftV + 140, maxRight - menuWidth);
        }

        const bottomPx = vh - triggerR.top + (showCaret ? 2 : -6);
        const maxHeightPx = Math.max(
          160,
          triggerR.top - margin - (showCaret ? 12 : 0)
        );

        const triggerCenterX = triggerR.left + triggerR.width / 2;
        const rawCaretLeft = triggerCenterX - menuLeftV;
        const inset = Math.min(
          caretEdgeInset,
          Math.max(4, menuWidth / 2 - 1)
        );
        const caretLeftPx = Math.min(
          Math.max(inset, rawCaretLeft),
          menuWidth - inset
        );

        setPanelLayout({
          leftPx: menuLeftV,
          widthPx: menuWidth,
          caretLeftPx,
          bottomPx,
          maxHeightPx,
        });
        return;
      }

      const offsetParent = outerEl.offsetParent as HTMLElement | null;
      if (!offsetParent) {
        return;
      }
      const parentR = offsetParent.getBoundingClientRect();

      const baseWidth = Math.min(panelWidthPx, vw - 2 * margin);
      let menuLeftV = triggerR.right - baseWidth;
      let menuWidth = baseWidth;

      if (menuLeftV < margin) {
        menuLeftV = margin;
        menuWidth = maxRight - menuLeftV;
      } else if (menuLeftV + menuWidth > maxRight) {
        menuLeftV = maxRight - menuWidth;
        if (menuLeftV < margin) {
          menuLeftV = margin;
          menuWidth = maxRight - menuLeftV;
        }
      }

      const leftInParent = menuLeftV - parentR.left + offsetParent.scrollLeft;

      const triggerCenterX = triggerR.left + triggerR.width / 2;
      const rawCaretLeft = triggerCenterX - menuLeftV;
      const inset = Math.min(caretEdgeInset, Math.max(4, menuWidth / 2 - 1));
      const caretLeftPx = Math.min(
        Math.max(inset, rawCaretLeft),
        menuWidth - inset
      );

      setPanelLayout({ leftPx: leftInParent, widthPx: menuWidth, caretLeftPx });
    };

    updateLayout();
    const raf = requestAnimationFrame(() => {
      updateLayout();
      requestAnimationFrame(updateLayout);
    });
    window.addEventListener('resize', updateLayout);
    window.addEventListener('scroll', updateLayout, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updateLayout);
      window.removeEventListener('scroll', updateLayout);
    };
  }, [
    triggerRef,
    panelWidthPx,
    placement,
    showCaret,
    caretEdgeInset,
    viewportMarginPx,
    layoutDependency,
  ]);

  useEffect(() => {
    if (!outsidePointerBoundaryRef || !onDismiss) {
      return;
    }
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const el = outsidePointerBoundaryRef.current;
      const target = e.target as Node | null;
      if (el && target && !el.contains(target)) {
        onDismiss();
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onDismiss();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onDismiss, outsidePointerBoundaryRef]);

  const caretStyle: React.CSSProperties = {
    position: 'absolute',
    zIndex: 3,
    pointerEvents: 'none',
    display: 'block',
    transform: 'translateX(-50%)',
    filter:
      placement === 'top' || placement === 'top-right'
        ? 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.07))'
        : 'drop-shadow(0 -2px 4px rgba(0, 0, 0, 0.07))',
    ...(placement === 'top' || placement === 'top-right'
      ? { bottom: -8 }
      : { top: -8 }),
    ...(panelLayout != null
      ? { left: panelLayout.caretLeftPx }
      : { left: '50%' }),
  };

  return (
    <Box
      ref={outerRef}
      id={id}
      role="menu"
      aria-label={ariaLabel}
      sx={{
        position:
          placement === 'top' || placement === 'top-right'
            ? 'fixed'
            : 'absolute',
        ...(placement === 'top' || placement === 'top-right'
          ? {
              bottom:
                panelLayout?.bottomPx != null
                  ? `${panelLayout.bottomPx}px`
                  : '100%',
            }
          : {
              top: '100%',
              mt: showCaret ? '2px' : '-6px',
            }),
        zIndex: 30,
        overflow: 'visible',
        ...(panelLayout != null
          ? {
              left: `${panelLayout.leftPx}px`,
              right: 'auto',
              width: `${panelLayout.widthPx}px`,
            }
          : {
              right: 0,
              width: `${panelWidthPx}px`,
              maxWidth: `calc(100vw - ${viewportMarginPx * 2}px)`,
            }),
      }}
    >
      {showCaret && (
        <svg
          width={14}
          height={8}
          viewBox="0 0 14 8"
          aria-hidden
          style={caretStyle}
        >
          <polygon
            points={
              placement === 'top' || placement === 'top-right'
                ? '0,0 7,8 14,0'
                : '0,8 7,0 14,8'
            }
            fill={colors.navbar}
          />
        </svg>
      )}
      <Box
        sx={{
          position: 'relative',
          marginTop: 0,
          display: 'flex',
          flexDirection: 'column',
          maxHeight:
            panelLayout?.maxHeightPx != null
              ? `min(${panelLayout.maxHeightPx}px, 412px)`
              : 'min(70vh, 412px)',
          overflow: 'hidden',
          bg: 'navbar',
          borderRadius: 'default',
          boxShadow: [
            '0 -3px 10px rgba(0, 0, 0, 0.07)',
            '0 2px 4px rgba(0, 0, 0, 0.06)',
            '0 6px 16px rgba(0, 0, 0, 0.1)',
          ].join(', '),
          zIndex: 1,
        }}
      >
        {title != null && (
          <Box
            flexShrink={0}
            px={3}
            py={2}
            sx={{ borderBottom: '1px solid', borderColor: 'tableBorder' }}
          >
            {typeof title === 'string' ? (
              <Text variant="body1" fontWeight="bold" color="text">
                {title}
              </Text>
            ) : (
              title
            )}
          </Box>
        )}
        <Box
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            minWidth: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin',
            scrollbarColor: `${colors.base.gray[500]} transparent`,
            '&::-webkit-scrollbar': {
              width: '10px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: colors.base.gray[400],
              borderRadius: '999px',
              border: '3px solid transparent',
              backgroundClip: 'padding-box',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: colors.base.gray[500],
              backgroundClip: 'padding-box',
            },
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AnchorMenu;
